import React from 'react'
import TableInc from '../../../shared-ui/TableInc'

// Rows per page. `limit` starts at the first option so the table's footer and
// the request it triggers can never disagree about the page size.
export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]
export const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[0]

// A TableInc whose rows arrive one page at a time from the server.
//
// material-table decides how to paginate from the SHAPE of `data`: a plain
// array is taken to be the whole result set and is sliced locally. Hand it one
// server page and it is stuck there — the footer counts only the rows it was
// given, so "next" is disabled before it can be clicked, and even when a fetch
// does happen the slice for page 2 runs off the end of a ten-row array and the
// table comes back empty.
//
// `totalCount` alone does not fix it: material-table only trusts an outside
// count when `page` is supplied as well (its isOutsidePageNumbers check wants
// both), and in that mode it stops re-slicing and renders the rows as given.
// Supplying the pair is the whole point of this component — anything paging a
// TableInc from a server should go through it rather than passing `totalCount`
// on its own and appearing to work.
class ServerPagedTable extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      // The page last ASKED for, 0-based as material-table counts pages. Only a
      // fallback for the footer: what it displays comes from the server echo
      // below, because this value is set before the rows it describes exist.
      requestedPage: 0,
      // Initial page size only — a later change to props.pageSize is ignored.
      limit: props.pageSize || DEFAULT_PAGE_SIZE
    }
    this.handlePageChange = this.handlePageChange.bind(this)
  }

  // The page actually on screen.
  //
  // `requestedPage` is committed before the response arrives, so on a failed
  // fetch it describes rows that were never rendered: the reducers leave the
  // previous payload in place and only set an error, which would leave the footer
  // reading "11-20 of 34" over page 1's rows with nothing to correct it.
  // material-table cannot self-correct either — its own past-the-end guard keys
  // off an internal page this mode deliberately pins.
  //
  // So prefer the page the server echoed back alongside the rows (the endpoints
  // return { data, total, page, limit }), which by construction describes what is
  // displayed. This is the shape GroupPolicies already uses. The requested page
  // is the fallback for a response that omits the echo.
  displayedPage() {
    // A 1-based page is always at least 1, and Number() turns a missing echo into
    // NaN and a null one into 0 — neither of which is greater than zero. So this
    // single test covers every unusable value and falls back to what was asked for.
    const echoed = Number(this.props.page)
    if (echoed > 0) {
      // The endpoints are 1-based; material-table is 0-based.
      return echoed - 1
    }
    return this.state.requestedPage
  }

  componentDidMount() {
    this.fetch(0, this.state.limit)
  }

  // resetKey is whatever selects the result set — the dashboard tab, a filter.
  // Once it changes the current offset means nothing, so start again at the
  // first page instead of asking for page 4 of a list that may only have one.
  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey) {
      this.fetch(0, this.state.limit)
    }
  }

  fetch(page, limit) {
    this.setState({ requestedPage: page, limit })
    // The endpoints are 1-based; material-table is 0-based.
    this.props.onFetch(page + 1, limit)
  }

  // Both footer controls land here: material-table reports a rows-per-page
  // change as onChangePage(0, newSize), so there is nothing to add for the
  // rows-per-page select beyond re-reading the first page at the new size.
  handlePageChange(page, pageSize) {
    this.fetch(page, pageSize)
  }

  render() {
    const data = Array.isArray(this.props.data) ? this.props.data : []
    const total = this.props.total
    const page = this.displayedPage()
    return (
      <TableInc
        columns={this.props.columns}
        data={data}
        isLoading={this.props.isLoading}
        page={page}
        // Without a server count, describe the rows seen so far rather than this
        // page's length alone: on page 2 of 10-row pages, data.length would read
        // "11-10 of 10" and offer a page that does not exist.
        totalCount={
          typeof total === 'number'
            ? total
            : page * this.state.limit + data.length
        }
        onChangePage={this.handlePageChange}
        options={Object.assign({}, this.props.options, {
          pageSize: this.state.limit,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
          // material-table's search filters the rows it currently holds, which
          // here is one server page. Combined with a server total the footer
          // counts every match in the result set while showing only the matches on
          // this page — "1-10 of 34" above a single row — and leaves Next enabled,
          // so clicking it re-applies the filter to unrelated rows. The component
          // owns this rather than trusting each caller, because the two features
          // cannot be correct together until search is pushed server-side (the
          // groups endpoint takes group_name / group_number).
          search: false
        })}
        localization={this.props.localization}
      />
    )
  }
}

export default ServerPagedTable
